import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import { Card, CardContent } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { toast, Toaster } from 'sonner';
import { ChevronLeft, Users, Coffee, LogOut, LogIn } from 'lucide-react';
import analytics from './utils/analytics';
import LateAlertModal from './components/TimeManagement/LateAlertModal';
import OvertimeRequestModal from './components/TimeManagement/OvertimeRequestModal';
import EarlyCheckoutBlockModal from './components/TimeManagement/EarlyCheckoutBlockModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to join classnames
function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}

// Get initials from name
function getInitials(firstName, lastName) {
  const first = firstName?.[0] ?? '';
  const last = lastName?.[0] ?? first;
  return (first + last).toUpperCase();
}

// Clamp text component for long names
function ClampedText({ children, lines = 2, className = '' }) {
  return (
    <div
      className={className}
      style={{
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: lines,
        overflow: 'hidden',
        wordBreak: 'break-word',
      }}
    >
      {children}
    </div>
  );
}

const AttendancePage = () => {
  const { locationId } = useParams();
  const [location, setLocation] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [pin, setPin] = useState('');
  const [shake, setShake] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(true);

  // Time management modals
  const [showLateAlert, setShowLateAlert] = useState(false);
  const [lateMinutes, setLateMinutes] = useState(0);
  const [showOvertimeRequest, setShowOvertimeRequest] = useState(false);
  const [overtimeData, setOvertimeData] = useState(null);
  const [showEarlyCheckoutBlock, setShowEarlyCheckoutBlock] = useState(false);
  const [earlyCheckoutData, setEarlyCheckoutData] = useState(null);
  const [employeeStatus, setEmployeeStatus] = useState(null); // 'checked_in', 'on_break', 'checked_out'
  const [statusLoading, setStatusLoading] = useState(false);

  // Fetch location details
  useEffect(() => {
    const fetchLocation = async () => {
      try {
        const response = await axios.get(`${API}/locations/${locationId}`);
        setLocation(response.data);
        analytics.trackPageView(`Attendance Kiosk - ${response.data.name}`, window.location.href);
      } catch (error) {
        console.error('Error fetching location:', error);
        toast.error('Error loading location details');
        analytics.trackError('location_fetch', error.message);
      }
    };

    if (locationId) {
      fetchLocation();
    }
  }, [locationId]);

  // Fetch all employees for this location
  useEffect(() => {
    const fetchEmployees = async () => {
      setIsLoadingEmployees(true);
      try {
        // Fetch all initials first
        const initialsResponse = await axios.get(`${API}/employees/initials/${locationId}`);
        const initials = initialsResponse.data.initials || [];
        
        // Fetch employees for each initial
        const allEmployees = [];
        for (const initial of initials) {
          try {
            const response = await axios.get(`${API}/employees/by-initial/${locationId}/${initial}`);
            if (response.data.employees) {
              allEmployees.push(...response.data.employees);
            }
          } catch (e) {
            console.error(`Error fetching employees for initial ${initial}:`, e);
          }
        }
        
        setEmployees(allEmployees);
      } catch (error) {
        console.error('Error fetching employees:', error);
        toast.error('Error loading employees');
      } finally {
        setIsLoadingEmployees(false);
      }
    };

    if (locationId) {
      fetchEmployees();
    }
  }, [locationId]);

  // Filter employees based on search query
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((emp) => {
      const fullName = `${emp.first_name} ${emp.last_name}`.toLowerCase();
      return fullName.includes(q);
    });
  }, [query, employees]);

  // Show all filtered employees in grid
  const visible = filtered;

  // Open bottom sheet for selected employee
  const openSheet = async (employee) => {
    setSelected(employee);
    setPin('');
    setEmployeeStatus(null);
    setStatusLoading(true);
    try {
      const res = await axios.get(`${API}/kiosk/employee-status/${locationId}/${employee.id}`);
      setEmployeeStatus(res.data.status);
    } catch {
      setEmployeeStatus('checked_out');
    } finally {
      setStatusLoading(false);
    }
  };

  // Close bottom sheet
  const closeSheet = () => {
    setSelected(null);
    setPin('');
    setEmployeeStatus(null);
  };

  // PIN keypad handlers
  const appendDigit = (d) => {
    if (pin.length >= 4) return;
    setPin((p) => p + d);
  };

  const backspace = () => setPin((p) => p.slice(0, -1));

  // Handle kiosk submission (check-in, check-out, break_start, break_end)
  const handleSubmit = async (action = 'toggle') => {
    if (!selected || pin.length !== 4) {
      setShake(true);
      setTimeout(() => setShake(false), 450);
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/kiosk/submit/${locationId}`, {
        location_id: locationId,
        first_name: selected.first_name,
        last_name: selected.last_name,
        pin: pin.trim(),
        action: action
      });

      // Check for late alert
      if (response.data.alert) {
        const alertParts = response.data.alert.split('|');
        if (alertParts[0] === 'LATE_ALERT') {
          const match = alertParts[1].match(/(\d+) minutes/);
          if (match) {
            setLateMinutes(parseInt(match[1]));
            setShowLateAlert(true);
          }
        }
      }

      if (response.data.success) {
        analytics.trackAttendanceAction('attendance', locationId);
        
        if (!response.data.alert) {
          toast.success(response.data.message);
        }
        
        // Auto-close after success
        setTimeout(() => {
          closeSheet();
        }, 2000);
      } else {
        toast.error('Attendance recording failed');
      }

    } catch (error) {
      const message = error.response?.data?.detail || 'An error occurred';
      
      // Handle time management errors (403)
      if (error.response?.status === 403) {
        const messageParts = message.split('|');
        
        if (messageParts[0] === 'EARLY_CHECKIN_BLOCKED' && messageParts[2] === 'REQUIRE_OVERTIME') {
          const match = messageParts[1].match(/(\d+) minutes early.*starts at (\d{2}:\d{2})/);
          if (match) {
            const now = new Date();
            const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
            setOvertimeData({
              employeeId: selected.id,
              employeeName: `${selected.first_name} ${selected.last_name}`,
              locationId: locationId,
              requestedTime: currentTime,
              scheduledStart: match[2],
              minutesEarly: parseInt(match[1])
            });
            setShowOvertimeRequest(true);
          }
        } else if (messageParts[0] === 'EARLY_CHECKOUT_BLOCKED') {
          const match = messageParts[1].match(/scheduled until (\d{2}:\d{2})/);
          if (match) {
            setEarlyCheckoutData({
              scheduledEnd: match[1]
            });
            setShowEarlyCheckoutBlock(true);
          }
        } else {
          toast.error(message);
        }
      } else if (error.response?.status === 401) {
        toast.error('Incorrect PIN - Please try again');
        setPin('');
        setShake(true);
        setTimeout(() => setShake(false), 450);
      } else if (error.response?.status === 423) {
        toast.error('Account temporarily locked due to too many incorrect attempts');
        closeSheet();
      } else if (error.response?.status === 400) {
        toast.error(message);
      } else if (error.response?.status === 429) {
        toast.error('Please wait before trying again');
      } else {
        toast.error(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-zinc-950 via-zinc-950 to-zinc-900 text-zinc-50">
      <div className="mx-auto max-w-[430px] px-4 pb-10 pt-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-emerald-500/15 ring-1 ring-emerald-400/20">
            <Users className="h-5 w-5 text-emerald-300" />
          </div>
          <div>
            <h1 className="text-lg font-semibold tracking-tight">
              {location?.name || 'Select staff'}
            </h1>
            <p className="text-xs text-zinc-400">Tap your card, then enter your PIN</p>
          </div>
        </div>

        {/* Search */}
        <div className="mt-5">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name"
            className="h-11 rounded-2xl border-zinc-800 bg-zinc-900/60 text-zinc-100 placeholder:text-zinc-500 focus-visible:ring-emerald-400/40"
          />
        </div>

        {/* Loading State */}
        {isLoadingEmployees && (
          <div className="mt-8 text-center text-zinc-400">
            <div className="animate-pulse">Loading employees...</div>
          </div>
        )}

        {/* Grid 2x2 */}
        {!isLoadingEmployees && (
          <div className="mt-5">
            <div
              className="grid gap-3"
              style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}
            >
              {visible.map((emp, idx) => (
                <motion.div
                  key={emp.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, delay: Math.min(idx * 0.02, 0.12) }}
                >
                  <button onClick={() => openSheet(emp)} className="w-full text-left">
                    <Card className="overflow-hidden rounded-3xl border-zinc-800 bg-zinc-900/50 shadow-sm">
                      <CardContent className={cn('p-3', 'h-28')}>
                        <div className="flex h-full flex-col justify-between">
                          <div className="flex items-start gap-2 min-w-0">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-500/10 ring-1 ring-emerald-400/15">
                              <span className="text-sm font-semibold text-emerald-200">
                                {getInitials(emp.first_name, emp.last_name)}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <ClampedText
                                lines={2}
                                className="font-semibold text-zinc-100 leading-tight text-[14px]"
                              >
                                {emp.first_name} {emp.last_name}
                              </ClampedText>
                            </div>
                          </div>

                          <div className="mt-2 text-[11px] text-zinc-500">Tap to sign in / out</div>
                        </div>
                      </CardContent>
                    </Card>
                  </button>
                </motion.div>
              ))}
            </div>
            
            {/* Show message if no employees found */}
            {visible.length === 0 && !isLoadingEmployees && (
              <div className="mt-8 text-center text-zinc-400">
                {query ? 'No employees found matching your search' : 'No employees at this location'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom sheet for PIN entry */}
      <AnimatePresence>
        {selected && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/60 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeSheet}
            />

            <motion.div
              className="fixed inset-x-0 bottom-0 mx-auto w-full max-w-[430px] z-50"
              initial={{ y: 420 }}
              animate={{ y: 0 }}
              exit={{ y: 420 }}
              transition={{ type: 'spring', stiffness: 240, damping: 28 }}
            >
              <div className="rounded-t-[28px] border border-zinc-800 bg-zinc-950/95 shadow-2xl backdrop-blur">
                <div className="px-4 pb-5 pt-3">
                  <div className="mx-auto mb-3 h-1.5 w-12 rounded-full bg-zinc-800" />

                  <div className="flex items-center gap-3">
                    <button
                      onClick={closeSheet}
                      className="flex h-9 w-9 items-center justify-center rounded-2xl bg-zinc-900/60 text-zinc-300 ring-1 ring-zinc-800 hover:bg-zinc-800/60"
                      aria-label="Back"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>

                    <div>
                      <div className="text-sm font-semibold">Enter PIN</div>
                      <div className="text-xs text-zinc-400">
                        {selected.first_name} {selected.last_name}
                      </div>
                    </div>
                  </div>

                  <motion.div
                    className={cn(
                      'mt-4 rounded-3xl border border-zinc-800 bg-zinc-900/50 p-4',
                      shake && 'border-red-400/40'
                    )}
                    animate={shake ? { x: [0, -8, 8, -6, 6, 0] } : { x: 0 }}
                    transition={{ duration: 0.45 }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-zinc-400">PIN</div>
                      <div className="text-xs text-zinc-500">4 digits</div>
                    </div>
                    <div className="mt-3 flex items-center justify-center gap-2">
                      {Array.from({ length: 4 }).map((_, i) => {
                        const filled = i < pin.length;
                        return (
                          <div
                            key={i}
                            className={cn(
                              'h-3 w-3 rounded-full ring-1 transition',
                              filled
                                ? 'bg-emerald-300 ring-emerald-300/60'
                                : 'bg-zinc-800 ring-zinc-700'
                            )}
                          />
                        );
                      })}
                    </div>
                  </motion.div>

                  {/* PIN Keypad */}
                  <div className="mt-4 grid grid-cols-3 gap-2">
                    {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
                      <button
                        key={d}
                        onClick={() => appendDigit(d)}
                        disabled={isLoading}
                        className="h-12 rounded-3xl border border-zinc-800 bg-zinc-900/55 text-base font-semibold text-zinc-100 hover:bg-zinc-800/60 active:scale-[0.98] disabled:opacity-50"
                      >
                        {d}
                      </button>
                    ))}

                    <button
                      onClick={() => setPin('')}
                      disabled={isLoading}
                      className="h-12 rounded-3xl border border-zinc-800 bg-zinc-900/40 text-sm text-zinc-300 hover:bg-zinc-800/50 active:scale-[0.98] disabled:opacity-50"
                    >
                      Clear
                    </button>

                    <button
                      onClick={() => appendDigit('0')}
                      disabled={isLoading}
                      className="h-12 rounded-3xl border border-zinc-800 bg-zinc-900/55 text-base font-semibold text-zinc-100 hover:bg-zinc-800/60 active:scale-[0.98] disabled:opacity-50"
                    >
                      0
                    </button>

                    <button
                      onClick={backspace}
                      disabled={isLoading}
                      className="h-12 rounded-3xl border border-zinc-800 bg-zinc-900/40 text-sm text-zinc-300 hover:bg-zinc-800/50 active:scale-[0.98] disabled:opacity-50"
                    >
                      Del
                    </button>
                  </div>

                  {/* Status indicator */}
                  {!statusLoading && employeeStatus && (
                    <div className={cn(
                      'mt-3 rounded-2xl px-4 py-2.5 text-center text-xs font-medium',
                      employeeStatus === 'checked_in' && 'bg-emerald-500/10 text-emerald-300 ring-1 ring-emerald-400/20',
                      employeeStatus === 'on_break' && 'bg-amber-500/10 text-amber-300 ring-1 ring-amber-400/20',
                      employeeStatus === 'checked_out' && 'bg-zinc-800/60 text-zinc-400 ring-1 ring-zinc-700'
                    )}>
                      {employeeStatus === 'checked_in' && 'Currently signed in'}
                      {employeeStatus === 'on_break' && 'Currently on break'}
                      {employeeStatus === 'checked_out' && 'Not signed in'}
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="mt-4 flex flex-col gap-2">
                    {/* Primary action row */}
                    <div className="flex gap-2">
                      <Button
                        variant="secondary"
                        onClick={closeSheet}
                        disabled={isLoading}
                        className="h-11 flex-1 rounded-3xl bg-zinc-900/60 text-zinc-200 ring-1 ring-zinc-800 hover:bg-zinc-800/60"
                        data-testid="cancel-btn"
                      >
                        Cancel
                      </Button>

                      {/* Sign In / Sign Out button */}
                      {employeeStatus !== 'on_break' && (
                        <Button
                          onClick={() => handleSubmit('toggle')}
                          disabled={pin.length !== 4 || isLoading}
                          className={cn(
                            'h-11 flex-1 rounded-3xl disabled:opacity-50',
                            employeeStatus === 'checked_in'
                              ? 'bg-rose-500/15 text-rose-200 ring-1 ring-rose-400/25 hover:bg-rose-500/20'
                              : 'bg-emerald-500/15 text-emerald-200 ring-1 ring-emerald-400/25 hover:bg-emerald-500/20'
                          )}
                          data-testid="toggle-btn"
                        >
                          {isLoading ? 'Processing...' : (
                            <>
                              {employeeStatus === 'checked_in' ? (
                                <><LogOut className="w-4 h-4 mr-1.5" />Sign Out</>
                              ) : (
                                <><LogIn className="w-4 h-4 mr-1.5" />Sign In</>
                              )}
                            </>
                          )}
                        </Button>
                      )}

                      {/* End Break button (shown when on break) */}
                      {employeeStatus === 'on_break' && (
                        <Button
                          onClick={() => handleSubmit('break_end')}
                          disabled={pin.length !== 4 || isLoading}
                          className="h-11 flex-1 rounded-3xl bg-emerald-500/15 text-emerald-200 ring-1 ring-emerald-400/25 hover:bg-emerald-500/20 disabled:opacity-50"
                          data-testid="end-break-btn"
                        >
                          {isLoading ? 'Processing...' : (
                            <><Coffee className="w-4 h-4 mr-1.5" />End Break</>
                          )}
                        </Button>
                      )}
                    </div>

                    {/* Break button row (only when checked in, not on break, AND break_deduction_enabled is ON) */}
                    {employeeStatus === 'checked_in' && location?.customization?.break_deduction_enabled && (
                      <Button
                        onClick={() => handleSubmit('break_start')}
                        disabled={pin.length !== 4 || isLoading}
                        className="h-11 w-full rounded-3xl bg-amber-500/10 text-amber-200 ring-1 ring-amber-400/20 hover:bg-amber-500/15 disabled:opacity-50"
                        data-testid="start-break-btn"
                      >
                        {isLoading ? 'Processing...' : (
                          <><Coffee className="w-4 h-4 mr-1.5" />Start Break</>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <Toaster position="top-center" />

      {/* Time Management Modals */}
      {showLateAlert && (
        <LateAlertModal
          minutesLate={lateMinutes}
          onClose={() => setShowLateAlert(false)}
        />
      )}

      {showOvertimeRequest && overtimeData && (
        <OvertimeRequestModal
          employeeId={overtimeData.employeeId}
          employeeName={overtimeData.employeeName}
          locationId={overtimeData.locationId}
          requestedTime={overtimeData.requestedTime}
          scheduledStart={overtimeData.scheduledStart}
          minutesEarly={overtimeData.minutesEarly}
          onClose={() => {
            setShowOvertimeRequest(false);
            closeSheet();
          }}
          onRequestSubmitted={() => {
            // Keep modal open while polling for approval
          }}
          onStartAnyway={() => {
            setShowOvertimeRequest(false);
            window.location.reload();
          }}
        />
      )}

      {showEarlyCheckoutBlock && earlyCheckoutData && (
        <EarlyCheckoutBlockModal
          scheduledEnd={earlyCheckoutData.scheduledEnd}
          onClose={() => {
            setShowEarlyCheckoutBlock(false);
            closeSheet();
          }}
        />
      )}
    </div>
  );
};

export default AttendancePage;
